import { useTranslation } from 'react-i18next';
import { TriangleAlert, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { Alert, AlertAction, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ROUTES } from '@/router/constant/routes';
import { usePermissions } from '@/hooks/use-permissions';
import { PERMISSION } from '@/features/users-roles/constants';
import { useCurrentOrganization } from '@/features/organization/hooks/use-current-organization';

import { useBillingUsage } from '../hooks/use-billing-usage';
import { getResourceMeta } from '../constants/resource-registry';
import {
    useDismissedResourceKeys,
    useUsageAlertDismissalsStore,
} from '../stores/usage-alert-dismissals.store';
import { computeResource } from '../utils/resource-health';

/**
 * Route scoping for CAPACITY resources — limits on how many of a thing you may
 * own. Exhausting one only blocks work inside that one module, so following the
 * user around the whole app is noise ("Sequences exhausted" while they read
 * Contacts). Each key lists the path prefixes where the resource is actually
 * consumed; the billing page always shows everything.
 *
 * THROUGHPUT resources (`sms_sends`, `email_sends`, `voice_minutes`,
 * `ai_credits`, `storage_mb`) are deliberately ABSENT: running out of sends or
 * credits breaks campaigns, sequences, the inbox and the dialer at once, so
 * those keep the app-wide heads-up. Anything not listed here stays global, so
 * a newly added resource errs toward being seen rather than silently hidden.
 */
const CAPACITY_RESOURCE_ROUTES: Readonly<Record<string, readonly string[]>> = {
    active_sequences: [ROUTES.SEQUENCES],
    contacts: [ROUTES.CONTACTS],
    static_segments: [ROUTES.SEGMENTS],
    dynamic_segments: [ROUTES.SEGMENTS],
    tags: [ROUTES.TAGS, ROUTES.SETTINGS_TAGS],
    active_users: [ROUTES.SETTINGS_USERS],
    custom_fields: [ROUTES.SETTINGS_MODULE_SETUP],
    campaigns: [ROUTES.CAMPAIGNS],
    polls: [ROUTES.POLLS],
    join_links: [ROUTES.JOIN_LINKS],
    join_keywords: [ROUTES.JOIN_KEYWORDS],
    // Short links render inside the Join Links page (`/links`).
    short_links: [ROUTES.JOIN_LINKS],
    quick_messages: [ROUTES.INBOX],
    sms_templates: [ROUTES.TEMPLATES, ROUTES.SETTINGS_SMS_TEMPLATES, ROUTES.SETTINGS_TEMPLATES],
    email_templates: [ROUTES.TEMPLATES, ROUTES.SETTINGS_TEMPLATES],
    webhook_endpoints: [ROUTES.SETTINGS_WEBHOOKS],
    api_keys: [ROUTES.SETTINGS_API_KEYS],
    knowledge_bases: [ROUTES.KNOWLEDGE_BASES],
    ai_agents: [ROUTES.AI_EMPLOYEES],
    phone_numbers: [ROUTES.CHANNELS, ROUTES.SETTINGS_CHANNELS],
    email_senders: [ROUTES.SETTINGS_EMAIL_SENDERS],
    meeting_types: [ROUTES.MEETING_TYPES],
    custom_modules: [ROUTES.SETTINGS_MODULE_SETUP, '/modules'],
};

/** True when this resource's warning belongs on the page currently open. */
function isRelevantOnRoute(resourceKey: string, pathname: string): boolean {
    // Billing is where you act on ANY limit — never hide a breach there.
    if (pathname.startsWith(ROUTES.BILLING)) return true;
    const scopes = CAPACITY_RESOURCE_ROUTES[resourceKey];
    if (!scopes) return true; // throughput / unknown → app-wide, as before
    return scopes.some((scope) => pathname.startsWith(scope));
}

/**
 * App-shell usage banner — surfaces a metered resource that has crossed its
 * warning (80%) or hard (100%) threshold. Throughput resources (voice minutes,
 * SMS, AI credits, …) appear on EVERY authenticated page, giving an org a
 * standing heads-up to top up before sends/calls start failing and
 * complementing the in-widget dial gate. Capacity limits are scoped to the
 * module they block, plus the billing page — see `CAPACITY_RESOURCE_ROUTES`.
 *
 * One alert per breached resource, each independently dismissable: an org that
 * has deliberately maxed out a resource it won't top up can silence just that
 * one and keep being warned about the rest. Dismissal is permanent per org
 * (see `usage-alert-dismissals.store`); the billing Usage tab owns the undo.
 *
 * Renders nothing while loading or when the usage endpoint is unavailable —
 * a missing snapshot must never read as an all-clear OR a false alarm; the
 * per-tab usage view (with retry) owns the error surface.
 */
export function GlobalUsageBanner() {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    // Only billing-capable members can read usage (and act on the CTA); skip the
    // request entirely for everyone else so this never 403s app-wide.
    //
    // DELIBERATELY kept on the rich billing:read usage summary rather than the
    // member-readable /billing/limits projection: the banner's only CTA is
    // "Manage plan" → /billing, which non-billing roles cannot act on — an
    // org-wide banner with a dead CTA would be pure noise for every agent.
    // Non-admins get their signal from the per-control limit locks and chips.
    const { hasPermission } = usePermissions();
    const { data } = useBillingUsage({ enabled: hasPermission(PERMISSION.BILLING_READ) });
    // Cache hit in practice — the sidebar org switcher fetches this app-wide.
    const { data: organization } = useCurrentOrganization();
    const orgId = organization?.id;
    const dismiss = useUsageAlertDismissalsStore((state) => state.dismiss);
    // Subscribing to the dismissal list (not just the action) is what makes the
    // X take effect: clicking it writes to the store, and this selector is the
    // only thing that re-renders the banner with the closed alert removed.
    const dismissedKeys = useDismissedResourceKeys(orgId);

    // Hold the banner back until the org is known: dismissals are org-scoped, so
    // rendering earlier would flash alerts the user has already closed.
    if (!data || !orgId) return null;

    const breached = data.resources
        .map((resource) => ({ resource, computed: computeResource(resource) }))
        .filter(({ computed }) => computed.health === 'warning' || computed.health === 'critical')
        .filter(({ resource }) => !dismissedKeys.includes(resource.resourceKey))
        .filter(({ resource }) => isRelevantOnRoute(resource.resourceKey, pathname));
    if (breached.length === 0) return null;

    return (
        <div className="flex flex-col gap-2 px-8 pt-4">
            {breached.map(({ resource, computed }) => {
                const isCritical = computed.health === 'critical';
                const tier = computed.alertTier;
                const label = t(
                    `billing.resources.labels.${resource.resourceKey}`,
                    getResourceMeta(resource.resourceKey).label,
                );

                return (
                    <Alert key={resource.resourceKey} variant={isCritical ? 'error' : 'warning'}>
                        <TriangleAlert />
                        <AlertTitle>
                            {isCritical
                                ? t('billing.globalBanner.criticalTitle', 'Resource limit reached')
                                : t('billing.globalBanner.warningTitle', 'Approaching plan limits')}
                        </AlertTitle>
                        <AlertDescription className="flex flex-col items-start gap-2">
                            {/* §8 ladder copy — escalates at 70 / 85 / 100% used. */}
                            {tier === 100
                                ? t(
                                      'billing.globalBanner.tier100',
                                      'Your {{resource}} are used up.',
                                      {
                                          resource: label,
                                      },
                                  )
                                : tier === 85
                                  ? t(
                                        'billing.globalBanner.tier85',
                                        "You're getting close to your {{resource}} limit.",
                                        { resource: label },
                                    )
                                  : t(
                                        'billing.globalBanner.tier70',
                                        "You're using most of your {{resource}}.",
                                        { resource: label },
                                    )}
                            <div className="flex flex-wrap gap-2">
                                <Button asChild variant="outline" size="sm">
                                    <Link to={`${ROUTES.BILLING}?focus=add-ons`}>
                                        {t('billing.globalBanner.buyMore', 'Buy more')}
                                    </Link>
                                </Button>
                                <Button asChild variant="outline" size="sm">
                                    <Link to={ROUTES.BILLING}>
                                        {t('billing.globalBanner.upgrade', 'Upgrade plan')}
                                    </Link>
                                </Button>
                            </div>
                        </AlertDescription>
                        <AlertAction>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon-xs"
                                className="size-6"
                                aria-label={t(
                                    'billing.globalBanner.dismiss',
                                    'Dismiss {{resource}} alert',
                                    {
                                        resource: label,
                                    },
                                )}
                                onClick={() => dismiss(orgId, resource.resourceKey)}
                            >
                                <X className="size-3.5" />
                            </Button>
                        </AlertAction>
                    </Alert>
                );
            })}
        </div>
    );
}
